/** <scapescene-frame-fields>: all editable fields of one storyboard frame.
 *
 * Attribute "position" (a frame number) selects the frame. Rendered inside the
 * editor screen's light DOM, below the filmstrip and above the sticky
 * footer. Autosaves field edits via a debounced Autosave<FramePatch>
 * and reports its draft state to the footer through a bubbling
 * "draft-state" CustomEvent.
 *
 * Skeleton is rebuilt only when the position changes (flushing the
 * previous frame's autosave first). Store-driven re-renders never
 * touch an input the user is currently focused in. */

import { store } from "../store";
import { assetKey } from "../data/client";
import type { ApiFrame, FramePatch } from "../data/types";
import { Autosave, type DraftState } from "../data/autosave";
import { frameCount, type PickList } from "../data/definitions";
import { pick, t, tn } from "../lib/i18n";

const FRAME_PICK_KEYS = [
  "howClose",
  "cameraPosition",
  "cameraMove",
  "feeling",
] as const;

type FramePickKey = (typeof FRAME_PICK_KEYS)[number];

const WHO_KEYS = ["whoRole", "whoWearing", "whoBody"] as const;

class WorkshopFrameFields extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["position"];
  }

  #unsubscribeStore: (() => void) | null = null;
  #position = 0;
  #autosave: Autosave<FramePatch> | null = null;
  #uploading = false;
  #uploadError = "";
  #builtWithDefinitions = false;

  #onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      this.#autosave?.flush().catch(() => undefined);
    }
  };

  connectedCallback(): void {
    this.#unsubscribeStore = store.onChange(() => this.#renderData());
    document.addEventListener("visibilitychange", this.#onVisibilityChange);
    this.#syncPosition();
  }

  disconnectedCallback(): void {
    this.#closeCamera();
    this.#unsubscribeStore?.();
    this.#unsubscribeStore = null;
    document.removeEventListener("visibilitychange", this.#onVisibilityChange);
    // Fire-and-forget: the frame we're leaving should still get its
    // last edits saved even though nothing awaits this.
    this.#autosave?.flush().catch(() => undefined);
  }

  attributeChangedCallback(name: string): void {
    if (name === "position") this.#syncPosition();
  }

  /** Public: flush any pending edits now (used by the footer/editor
   * screen before navigating away or on explicit "Save draft"). */
  async flush(): Promise<void> {
    await this.#autosave?.flush();
  }

  #syncPosition(): void {
    const raw = Number(this.getAttribute("position"));
    const position =
      raw >= 1 && raw <= frameCount(store.definitions) ? raw : 0;
    if (position === this.#position) return;

    const previousAutosave = this.#autosave;
    this.#closeCamera();
    this.#position = position;
    this.#uploading = false;
    this.#uploadError = "";
    this.#autosave = position
      ? new Autosave<FramePatch>(
          (patch) => store.patchFrame(position, patch),
          { onState: (state) => this.#emitDraftState(state) },
        )
      : null;

    // Flush the outgoing frame's edits after swapping in the new
    // autosave instance so nothing here depends on its completion.
    if (previousAutosave) {
      previousAutosave.flush().catch(() => undefined);
    }

    this.#build();
    this.#renderData();
  }

  #emitDraftState(state: DraftState): void {
    this.dispatchEvent(
      new CustomEvent("draft-state", {
        bubbles: true,
        composed: true,
        detail: { ...state },
      }),
    );
  }

  // -- skeleton -----------------------------------------------------------

  #build(): void {
    const definitions = store.definitions;
    const position = this.#position;
    if (!position || !definitions) {
      this.innerHTML = "";
      this.#builtWithDefinitions = false;
      return;
    }
    this.#builtWithDefinitions = true;

    const frameDef = definitions.frames.find((f) => f.position === position);
    const who = definitions.textFields.who;
    const whatHappens = definitions.textFields.whatHappens;
    const whereAreWe = definitions.textFields.whereAreWe;
    const pickLists = FRAME_PICK_KEYS.map((key) =>
      definitions.pickLists.find((p) => p.scope === "frame" && p.key === key),
    );
    // The worksheet decides how many "who" inputs there are; the extra
    // frame columns simply stay empty when it defines fewer than three.
    const whoInputs = who.inputs.filter((input) =>
      (WHO_KEYS as readonly string[]).includes(input.key),
    );
    const explanation = pick(frameDef?.explanation);

    this.innerHTML = `
      <div class="frame-fields">
        <header class="frame-fields__header">
          <h2>${frameDef ? `${t("frameWord")} ${position} — ${escapeHtml(pick(frameDef.name))}` : `${t("frameWord")} ${position}`}</h2>
          <p class="hint">${escapeHtml(pick(frameDef?.subtitle))}</p>
          <p class="frame-fields__question">${escapeHtml(pick(frameDef?.question))}</p>
          <p class="hint">${escapeHtml(pick(frameDef?.distanceHint))}</p>
          ${
            explanation
              ? `<details class="frame-fields__help">
                   <summary>${escapeHtml(t("moreHelp"))}</summary>
                   <p class="hint">${escapeHtml(explanation)}</p>
                 </details>`
              : ""
          }
          <p class="hint frame-fields__flow">${escapeHtml(t("howItWorksFrame"))}</p>
        </header>

        <div class="field frame-fields__sketch-field">
          <label class="field-label" data-role="sketch-label">${escapeHtml(t("sketchLabel"))}</label>
          <div class="sketch-tile" data-role="sketch-tile">
            <input type="file" accept="image/*" capture="environment" data-role="sketch-camera-input" hidden />
            <input type="file" accept="image/*" data-role="sketch-input" hidden />
            <img data-role="sketch-image" alt="${escapeHtml(t("sketchAlt"))}" hidden />
            <span class="sketch-tile__empty" data-role="sketch-empty">
              <svg class="sketch-tile__icon" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.5"/></svg>
              <span class="sketch-tile__title">${escapeHtml(t("sketchTitle"))}</span>
              <span class="sketch-tile__hint">${escapeHtml(pick(definitions.sketchHint))}</span>
              <span class="sketch-tile__why">${escapeHtml(t("sketchWhy"))}</span>
            </span>
            <span class="sketch-tile__busy" data-role="sketch-busy" hidden>${escapeHtml(t("uploading"))}</span>
            <div class="sketch-camera" data-role="sketch-camera" hidden>
              <video class="sketch-camera__video" data-role="sketch-camera-video" autoplay playsinline muted></video>
              <div class="sketch-camera__actions">
                <button type="button" class="ss-btn ss-btn--secondary" data-role="sketch-camera-cancel">${escapeHtml(t("cameraCancel"))}</button>
                <button type="button" class="ss-btn ss-btn--primary" data-role="sketch-camera-shutter">${escapeHtml(t("cameraShutter"))}</button>
              </div>
            </div>
          </div>
          <div class="sketch-actions">
            <button type="button" class="ss-btn ss-btn--primary sketch-actions__btn" data-role="sketch-take">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.5"/></svg>
              ${escapeHtml(t("takePhoto"))}
            </button>
            <button type="button" class="ss-btn ss-btn--secondary sketch-actions__btn" data-role="sketch-upload">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 16V4M6 10l6-6 6 6M4 20h16"/></svg>
              ${escapeHtml(t("uploadPhoto"))}
            </button>
          </div>
          <p class="hint" data-role="sketch-done" hidden>${escapeHtml(t("sketchDone"))}</p>
          <p class="hint" data-role="sketch-replace" hidden>${escapeHtml(t("sketchReplaceHint"))}</p>
          <p class="hint sketch-tile__error" data-role="sketch-error" hidden></p>
        </div>

        <p class="field-label frame-fields__step">${escapeHtml(t("fieldsLabel"))}</p>

        <div class="field">
          <label>${escapeHtml(pick(whereAreWe.label))}</label>
          <textarea
            data-field="whereAreWe"
            placeholder="${escapeHtml(pick(whereAreWe.hint))}"
          ></textarea>
        </div>

        <div class="field">
          <label>${escapeHtml(pick(who.label))}</label>
          ${whoInputs
            .map(
              (input) => `
            <input
              type="text"
              data-field="${input.key}"
              placeholder="${escapeHtml(pick(input.hint))}"
              class="frame-fields__who-input"
            />
          `,
            )
            .join("")}
        </div>

        <div class="field">
          <label>${escapeHtml(pick(whatHappens.label))}</label>
          <textarea
            data-field="whatHappens"
            placeholder="${escapeHtml(pick(whatHappens.hint))}"
          ></textarea>
        </div>

        ${pickLists
          .map((list, i) => this.#renderPickField(FRAME_PICK_KEYS[i], list))
          .join("")}
      </div>
    `;

    this.#bindEvents();
  }

  #renderPickField(key: FramePickKey, list: PickList | undefined): string {
    if (!list) return "";
    const options = list.options
      .map(
        (opt) =>
          `<option value="${escapeHtml(opt.value)}">${escapeHtml(pick(opt.label))}</option>`,
      )
      .join("");
    return `
      <div class="field">
        <label>${escapeHtml(pick(list.label))}</label>
        <select data-field="${key}">
          <option value="">—</option>
          ${options}
        </select>
      </div>
    `;
  }

  #bindEvents(): void {
    for (const key of WHO_KEYS) {
      const input = this.querySelector<HTMLInputElement>(
        `input[data-field="${key}"]`,
      );
      input?.addEventListener("input", () => {
        this.#autosave?.queue({ [key]: input.value } as Partial<FramePatch>);
      });
    }

    for (const key of ["whatHappens", "whereAreWe"] as const) {
      const textarea = this.querySelector<HTMLTextAreaElement>(
        `textarea[data-field="${key}"]`,
      );
      textarea?.addEventListener("input", () => {
        this.#autosave?.queue({
          [key]: textarea.value,
        } as Partial<FramePatch>);
      });
    }

    for (const key of FRAME_PICK_KEYS) {
      const select = this.querySelector<HTMLSelectElement>(
        `select[data-field="${key}"]`,
      );
      select?.addEventListener("change", () => {
        this.#autosave?.queue({ [key]: select.value } as Partial<FramePatch>);
        // Picks are cheap — save right away rather than waiting out
        // the debounce.
        this.#autosave?.flush().catch(() => undefined);
      });
    }

    const fileInput = this.querySelector<HTMLInputElement>(
      '[data-role="sketch-input"]',
    );
    const cameraInput = this.querySelector<HTMLInputElement>(
      '[data-role="sketch-camera-input"]',
    );
    for (const input of [fileInput, cameraInput]) {
      input?.addEventListener("change", () => {
        const file = input.files?.[0];
        input.value = "";
        if (file) this.#handleSketchPick(file);
      });
    }

    // Two explicit ways in. "Upload" is always the OS file picker (which
    // on phones also offers the camera roll). "Take a photo" uses the
    // native capture input on touch devices — the phone's own camera
    // app, best quality and no permission dance — and an in-widget
    // webcam view everywhere else, since desktops ignore `capture`.
    this.querySelector('[data-role="sketch-upload"]')?.addEventListener(
      "click",
      () => {
        if (!this.#uploading) fileInput?.click();
      },
    );
    this.querySelector('[data-role="sketch-take"]')?.addEventListener(
      "click",
      () => {
        if (this.#uploading) return;
        if (prefersNativeCapture()) cameraInput?.click();
        else void this.#openCamera();
      },
    );
    // Tapping the tile itself (empty or showing an image) is the same
    // as "Take a photo": the obvious thing to do with a big camera tile.
    this.querySelector('[data-role="sketch-tile"]')?.addEventListener(
      "click",
      (event) => {
        if (this.#uploading || this.#cameraStream) return;
        if ((event.target as HTMLElement).closest("button")) return;
        if (prefersNativeCapture()) cameraInput?.click();
        else void this.#openCamera();
      },
    );
    this.querySelector('[data-role="sketch-camera-cancel"]')?.addEventListener(
      "click",
      () => this.#closeCamera(),
    );
    this.querySelector('[data-role="sketch-camera-shutter"]')?.addEventListener(
      "click",
      () => void this.#snap(),
    );
  }

  // -- desktop webcam --------------------------------------------------------

  #cameraStream: MediaStream | null = null;

  async #openCamera(): Promise<void> {
    const panel = this.querySelector<HTMLElement>('[data-role="sketch-camera"]');
    const video = this.querySelector<HTMLVideoElement>(
      '[data-role="sketch-camera-video"]',
    );
    if (!panel || !video) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      this.#cameraFallback();
      return;
    }
    try {
      this.#cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 } },
        audio: false,
      });
    } catch {
      this.#cameraFallback();
      return;
    }
    video.srcObject = this.#cameraStream;
    panel.hidden = false;
    this.#uploadError = "";
    this.#renderSketchState();
  }

  /** No usable camera: say so and open the file picker instead. */
  #cameraFallback(): void {
    this.#uploadError = t("cameraUnavailable");
    this.#renderSketchState();
    this.querySelector<HTMLInputElement>('[data-role="sketch-input"]')?.click();
  }

  #closeCamera(): void {
    const panel = this.querySelector<HTMLElement>('[data-role="sketch-camera"]');
    const video = this.querySelector<HTMLVideoElement>(
      '[data-role="sketch-camera-video"]',
    );
    for (const track of this.#cameraStream?.getTracks() ?? []) track.stop();
    this.#cameraStream = null;
    if (video) video.srcObject = null;
    if (panel) panel.hidden = true;
  }

  async #snap(): Promise<void> {
    const video = this.querySelector<HTMLVideoElement>(
      '[data-role="sketch-camera-video"]',
    );
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    this.#closeCamera();
    if (blob) {
      this.#handleSketchPick(
        new File([blob], `sketch-${this.#position}.jpg`, { type: "image/jpeg" }),
      );
    }
  }

  async #handleSketchPick(file: File): Promise<void> {
    const position = this.#position;
    if (!position) return;
    this.#uploading = true;
    this.#uploadError = "";
    this.#renderSketchState();
    try {
      await store.uploadSketch(position, file);
    } catch (err) {
      this.#uploadError = errorMessage(err);
    } finally {
      this.#uploading = false;
      this.#renderSketchState();
    }
  }

  // -- data-driven updates --------------------------------------------------

  #renderData(): void {
    // The position may have been rejected before the definitions (and
    // so the real frame count) were known — re-check now.
    if (!this.#position && store.definitions) this.#syncPosition();
    const position = this.#position;
    if (!position) return;
    // Definitions may still be loading when the skeleton was first
    // built; rebuild once they arrive.
    if (!this.#builtWithDefinitions && store.definitions) {
      this.#build();
    }
    const frame = store.frame(position);
    const active = (this.getRootNode() as ShadowRoot | Document).activeElement;

    for (const key of WHO_KEYS) {
      const input = this.querySelector<HTMLInputElement>(
        `input[data-field="${key}"]`,
      );
      if (input && input !== active) {
        input.value = frame ? frame[key] : "";
      }
    }

    for (const key of ["whatHappens", "whereAreWe"] as const) {
      const textarea = this.querySelector<HTMLTextAreaElement>(
        `textarea[data-field="${key}"]`,
      );
      if (textarea && textarea !== active) {
        textarea.value = frame ? frame[key] : "";
      }
    }

    for (const key of FRAME_PICK_KEYS) {
      const select = this.querySelector<HTMLSelectElement>(
        `select[data-field="${key}"]`,
      );
      if (select && select !== active) {
        select.value = frame ? frame[key] : "";
      }
    }

    this.#renderSketchState(frame);
  }

  #renderSketchState(frame: ApiFrame | null = store.frame(this.#position)): void {
    const image = this.querySelector<HTMLImageElement>(
      '[data-role="sketch-image"]',
    );
    const empty = this.querySelector<HTMLElement>('[data-role="sketch-empty"]');
    const busy = this.querySelector<HTMLElement>('[data-role="sketch-busy"]');
    const errorEl = this.querySelector<HTMLElement>(
      '[data-role="sketch-error"]',
    );

    const replaceHint = this.querySelector<HTMLElement>(
      '[data-role="sketch-replace"]',
    );
    const doneHint = this.querySelector<HTMLElement>(
      '[data-role="sketch-done"]',
    );
    const tile = this.querySelector<HTMLElement>('[data-role="sketch-tile"]');

    // Once the generated picture exists it takes the tile over from the
    // sketch — a large preview right where the sketch was. Tapping it
    // still opens the file picker for a fresh sketch.
    const stillUrl = frame?.stillUrl ?? "";
    const imageUrl = stillUrl || frame?.sketchUrl || "";
    if (image) {
      if (imageUrl) {
        // assetKey: presigned URLs differ per poll for the same object —
        // only touch src when the underlying image actually changed.
        if (image.dataset.key !== assetKey(imageUrl)) {
          image.dataset.key = assetKey(imageUrl);
          image.src = imageUrl;
        }
        image.alt = stillUrl
          ? tn("frameRenderAlt", this.#position)
          : t("sketchAlt");
        image.hidden = false;
      } else {
        image.removeAttribute("src");
        delete image.dataset.key;
        image.hidden = true;
      }
    }
    if (empty) empty.hidden = Boolean(imageUrl) || this.#uploading;
    if (busy) busy.hidden = !this.#uploading;
    // The empty tile is the loudest thing on the screen — it is the step
    // people skip — and calms down once a sketch or first frame is in.
    tile?.classList.toggle("is-empty", !imageUrl);
    if (replaceHint) replaceHint.hidden = !stillUrl;
    if (doneHint) doneHint.hidden = !(frame?.sketchUrl && !stillUrl);
    if (errorEl) {
      errorEl.hidden = !this.#uploadError;
      errorEl.textContent = this.#uploadError;
    }
  }
}

/** Phones and tablets: hand "Take a photo" to the native camera app via
 * <input capture>. Desktops ignore `capture` (it would just open the
 * file picker), so they get the in-widget webcam view instead. */
function prefersNativeCapture(): boolean {
  try {
    return matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

function errorMessage(err: unknown): string {
  if (err && typeof err === "object") {
    const record = err as { detail?: unknown; message?: unknown };
    if (typeof record.detail === "string" && record.detail) return record.detail;
    if (typeof record.message === "string" && record.message)
      return record.message;
  }
  return t("sketchUploadFailed");
}

if (!customElements.get("scapescene-frame-fields")) {
  customElements.define("scapescene-frame-fields", WorkshopFrameFields);
}

export {};
